import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import useFields from '../../hooks/useFields';
import { formatValuesLoad } from '../index/values';
import InputText from '../../components/InputText';
import RadioButton from '../../components/RadioButton';
import { DescriptionForm } from '../../components/styled';
import InputMoney from '../../components/InputMoney';
import Select from '../../components/Select';
import FormButtons from '../../components/FormButtons';

const SeparationLine = styled.div`
  width: 100%;
  height: 4px;
  background: var(--background-background-disabled);
  margin-bottom: 24px;
  margin-top: 24px;
`;
interface BseSavingsAndDebtsProps {
  idRequest: number;
  save: (
    formName: string,
    fields: any,
    isComplete: boolean,
    save: boolean
  ) => void;
  next: () => void;
}

const BseSavingsAndDebts = (props: BseSavingsAndDebtsProps) => {
  const { idRequest, next, save } = props;
  const [validateForm, setValidateForm] = useState(0);
  const formBse = useSelector((state: any) => state.getFormBse);
  if (!formBse?.data) return null;
  const { formSavingsAndDebts } = formatValuesLoad(formBse?.data);
  const {
    fields,
    setFieldValue,
    setFieldError,
    formIsValid,
    formIsComplete,
    formIsEmpty,
    formIsIncomplete,
    formIsEmptyIncomplete
  } = useFields(formSavingsAndDebts);

  const {
    savingsPosee,
    savingsEntidad,
    savingsMonto,
    debtsPosee,
    debtsTipo,
    debtsEntidad,
    debtsMonto,
    debtsPagoMensual,
    debtsFechaFin
  } = fields;

  const dataInnecesaryForSavings = {
    debtsPosee,
    debtsTipo,
    debtsEntidad,
    debtsMonto,
    debtsPagoMensual,
    debtsFechaFin
  };

  const dataInnecesaryForDebts = {
    savingsPosee,
    savingsEntidad,
    savingsMonto
  };

  const dataInnecesaryForSavingsAndDebts = {
    savingsEntidad,
    savingsMonto,
    debtsTipo,
    debtsEntidad,
    debtsMonto,
    debtsPagoMensual,
    debtsFechaFin
  };

  const validateCaseIsIncomplete = () => {
    if (savingsPosee === 'Si' && debtsPosee === 'No') {
      return formIsIncomplete(dataInnecesaryForSavings);
    }
    if (debtsPosee === 'Si' && savingsPosee === 'No') {
      return formIsIncomplete(dataInnecesaryForDebts);
    }
    if (debtsPosee === 'No' && savingsPosee === 'No') {
      return formIsIncomplete(dataInnecesaryForSavingsAndDebts);
    }
    return formIsComplete;
  };

  const validateCaseEmptyIncomplete = () => {
    if (savingsPosee === 'Si' && debtsPosee === 'No') {
      return formIsEmptyIncomplete(dataInnecesaryForSavings);
    }
    if (debtsPosee === 'Si' && savingsPosee === 'No') {
      return formIsEmptyIncomplete(dataInnecesaryForDebts);
    }
    if (debtsPosee === 'No' && savingsPosee === 'No') {
      return formIsEmptyIncomplete(dataInnecesaryForSavingsAndDebts);
    }
    return formIsEmpty;
  };

  const saveFieldsClean = () => {
    let fieldsFormSavings = Object.assign(fields);

    if (savingsPosee === 'No') {
      fieldsFormSavings = Object.assign(fieldsFormSavings, {
        savingsPosee: 'No',
        savingsEntidad: '',
        savingsMonto: ''
      });
    }

    if (debtsPosee === 'No') {
      fieldsFormSavings = Object.assign(fieldsFormSavings, {
        debtsPosee: 'No',
        debtsTipo: '',
        debtsEntidad: '',
        debtsMonto: '',
        debtsPagoMensual: '',
        debtsFechaFin: ''
      });
    }

    return fieldsFormSavings;
  };

  const getProps = () => ({
    setFieldError,
    setFieldValue,
    validateOnStart: idRequest
      ? idRequest > 0 && !validateCaseEmptyIncomplete()
      : false,
    validateForm
  });

  const saveForm = () => {
    setValidateForm(validateForm + 1);
    save(
      'savingsAndDebts',
      saveFieldsClean(),
      validateCaseIsIncomplete(),
      true
    );
  };

  useEffect(() => {
    if (idRequest > 0 && !validateCaseEmptyIncomplete()) {
      save(
        'savingsAndDebts',
        saveFieldsClean(),
        validateCaseIsIncomplete(),
        false
      );
    }
  }, []);

  return (
    <div style={{ padding: '16px 24px' }}>
      <DescriptionForm>
        Registra los ahorros y deudas que tienen tú y tu familia hasta la fecha
      </DescriptionForm>
      <RadioButton
        question="¿Tienes ahorros?"
        firstLabel="Sí"
        secondLabel="No"
        firstValue="Si"
        secondValue="No"
        groupName="savingsPosee"
        // icon={iconSavings}
        value={savingsPosee}
        setFieldError={setFieldError}
        setFieldValue={setFieldValue}
        validateOnStart={false}
        validateForm={validateForm}
        isRequired
      >
        <div style={{ margin: '16px 0 0 0' }}>
          <InputText
            name="savingsEntidad"
            placeholder="Ingresar entidad"
            value={savingsEntidad}
            nameLabel="¿En qué entidad tienes tus ahorros?"
            {...getProps()}
            isRequired={savingsPosee === 'Yes'}
          />
          <div id="savingsMonto">
            <InputMoney
              name="savingsMonto"
              placeholder="Ingresar monto en soles (S/.)"
              value={savingsMonto}
              nameLabel="Monto"
              {...getProps()}
              validateForm={idRequest}
            />
          </div>
        </div>
        <div />
      </RadioButton>

      <SeparationLine />

      <RadioButton
        question="¿Tienes deudas pendientes?"
        firstLabel="Sí"
        secondLabel="No"
        firstValue="Si"
        secondValue="No"
        groupName="debtsPosee"
        value={debtsPosee}
        setFieldError={setFieldError}
        setFieldValue={setFieldValue}
        validateOnStart={false}
        validateForm={validateForm}
        isRequired
      >
        <div style={{ margin: '16px 0 0 0' }}>
          <Select
            name="debtsTipo"
            placeholder="selecciona tu tipo de deuda"
            value={debtsTipo}
            nameLabel="¿Qué tipo es?"
            optionsList={formBse?.data && formBse.data[58].dropDownList}
            {...getProps()}
          />
          <InputText
            name="debtsEntidad"
            placeholder="Ingresar entidad"
            value={debtsEntidad}
            nameLabel="¿En qué entidad tienes tus deudas?"
            {...getProps()}
          />
          <InputMoney
            name="debtsMonto"
            placeholder="Ingresar monto en soles (S/.)"
            value={debtsMonto}
            nameLabel="Monto"
            {...getProps()}
            validateForm={idRequest}
          />
          <InputMoney
            name="debtsPagoMensual"
            placeholder="Ingresar monto en soles (S/.)"
            value={debtsPagoMensual}
            nameLabel="Pago mensual"
            {...getProps()}
            validateForm={idRequest}
          />
          <InputText
            name="debtsFechaFin"
            placeholder="Ingresa la fecha de tu deuda"
            value={debtsFechaFin}
            nameLabel="Fin de deuda"
            {...getProps()}
          />
        </div>
        <div />
      </RadioButton>

      <FormButtons
        handlerSend={saveForm}
        handlerNext={next}
        formIsValid={formIsValid}
      />
    </div>
  );
};

export default BseSavingsAndDebts;
